import { ButtonGroup, Card, Heading, IconButton, LuIcon, toast } from "@overmap-ai/blocks"
import { type Asset, OrganizationAccessLevel, type Stored, submittedAtModelSortFn } from "@overmap-ai/common"
import {
	selectAssetTypeFieldValuesOfAsset,
	selectAssetTypeIdentifierValuesOfAsset,
	selectAttachmentsOfAsset,
	selectCommentsOfAsset,
	selectFormSubmissionsOfAsset,
	selectIssueAssociationsOfAsset,
	selectProceduresOfAsset,
} from "@overmap-ai/core"
import { type UUIDFile, type UUIDPromise } from "@overmap-ai/forms"
import { FileHandler } from "@tiptap/extension-file-handler"
import { Placeholder } from "@tiptap/extensions"
import { EditorContent, useEditor } from "@tiptap/react"
import { type FormEventHandler, memo, useCallback, useEffect, useMemo, useState } from "react"

import { ASSET_TIMELINE_ITEM_TYPES } from "../../constants"
import { AssetTimelineItemType } from "../../enums/assets"
import { modelIconMapping } from "../../mappings/icons"
import { sdk } from "../../sdk"
import { useShallowEqaulSelector } from "../../store"
import { BASE_EXTENSIONS, LINK_NODES, MENTION_EXTENSIONS } from "../../tiptap"
import { NewLineFloatingMenu, TextBubbleMenu } from "../../tiptap/components"
import { EditorContext } from "../../tiptap/context"
import { uuidFilesFromFiles } from "../../utils/file"
import { AttachmentButton } from "../Buttons/AttachmentButton"
import { CommentFileThumbnailList } from "../CommentFileThumbnailList/CommentFileThumbnailList"
import { AssetTimelineItemTypeMultiSelectDropdown } from "../DropdownMultiSelects/AssetTimelineItemTypeMultiSelectDropdown"
import { OrganizationAccessRequired } from "../OrganizationAccessRequired/AccessRequired"
import { AssetAttachmentTimelineItem } from "./AssetAttachmentTimelineItem"
import { AssetCommentTimelineItem } from "./AssetCommentTimelineItem"
import { AssetFieldValuesTimelineItem } from "./AssetFieldValuesTimelineItem"
import { AssetIdentifierValueTimelineItem } from "./AssetIdentifierValueTimelineItem"
import { AssetIssueAssociationTimelineItem } from "./AssetIssueAssociationTimelineItem"
import { AssetProcedureTimelineItem } from "./AssetProcedureTimelineItem"
import { AssetTimelineFormSubmissionItem } from "./AssetTimelineFormSubmissionItem"
import { CreatedAssetTimelineItem } from "./CreatedAssetTimelineItem"
import type { AssetTimelineItem, AssetTimelineSize } from "./typings"

interface AssetTimelineProps {
	asset: Stored<Asset>
	size: AssetTimelineSize
}

export const AssetTimeline = memo<AssetTimelineProps>((props) => {
	const { asset, size } = props

	const attachments = useShallowEqaulSelector(selectAttachmentsOfAsset(asset.uuid))
	const procedures = useShallowEqaulSelector(selectProceduresOfAsset(asset.uuid))
	const fieldValues = useShallowEqaulSelector(selectAssetTypeFieldValuesOfAsset(asset.uuid))
	const comments = useShallowEqaulSelector(selectCommentsOfAsset(asset.uuid))
	const issueAssociations = useShallowEqaulSelector(selectIssueAssociationsOfAsset(asset.uuid))
	const identifierValues = useShallowEqaulSelector(selectAssetTypeIdentifierValuesOfAsset(asset.uuid))
	const formSubmissions = useShallowEqaulSelector(selectFormSubmissionsOfAsset(asset.uuid))
	const [visibleItemTypes, setVisibleItemTypes] = useState<AssetTimelineItemType[]>(ASSET_TIMELINE_ITEM_TYPES)
	const [files, setFiles] = useState<UUIDFile[]>([])

	const timelineItems = useMemo(() => {
		const ret: AssetTimelineItem[] = [
			{
				model: asset,
				type: AssetTimelineItemType.CREATED,
			},
		]

		for (const attachment of attachments) {
			ret.push({ model: attachment, type: AssetTimelineItemType.ATTACHMENT })
		}

		for (const procedure of procedures) {
			ret.push({ model: procedure, type: AssetTimelineItemType.PROCEDURE })
		}

		for (const fieldValue of fieldValues) {
			ret.push({ model: fieldValue, type: AssetTimelineItemType.FIELD_VALUES })
		}

		for (const comment of comments) {
			ret.push({ model: comment, type: AssetTimelineItemType.COMMENT })
		}

		for (const issueAssociation of issueAssociations) {
			ret.push({ model: issueAssociation, type: AssetTimelineItemType.ISSUE_ASSOCIATION })
		}

		for (const identifierValue of identifierValues) {
			ret.push({ model: identifierValue, type: AssetTimelineItemType.IDENTIFIER_VALUE })
		}

		for (const formSubmission of formSubmissions) {
			ret.push({ model: formSubmission, type: AssetTimelineItemType.FORM_SUBMISSION })
		}

		return ret.toSorted((a, b) => submittedAtModelSortFn(a.model, b.model)).toReversed()
	}, [attachments, comments, fieldValues, identifierValues, issueAssociations, procedures, asset, formSubmissions])

	const filteredTimelineItems = useMemo(() => {
		const visibleItemTypesSet = new Set(visibleItemTypes)
		return timelineItems.filter(({ type }) => visibleItemTypesSet.has(type))
	}, [timelineItems, visibleItemTypes])

	const editor = useEditor({
		editorProps: {
			attributes: { class: "outline-none" },
		},
		extensions: [
			...BASE_EXTENSIONS,
			...LINK_NODES,
			...MENTION_EXTENSIONS,
			FileHandler.configure({
				onDrop: (_editor, files, _pos) => {
					setFiles((prev) => [...prev, ...uuidFilesFromFiles(files)])
				},
				onPaste: (_editor, files, _htmlContent) => {
					setFiles((prev) => [...prev, ...uuidFilesFromFiles(files)])
				},
			}),
			Placeholder.configure({
				placeholder: "Leave a comment",
			}),
		],
	})

	const handleAttachFiles = useCallback((files: File[]) => {
		setFiles((prev) => [...prev, ...uuidFilesFromFiles(files)])
	}, [])

	const handleDeleteFile = useCallback((file: UUIDFile | UUIDPromise<UUIDFile>) => {
		setFiles((prev) => prev.filter(({ uuid }) => uuid !== file.uuid))
	}, [])

	const handleCommentSubmit = useCallback<FormEventHandler<HTMLFormElement>>(
		(e) => {
			e.preventDefault()
			e.stopPropagation()

			if (editor.isEmpty && files.length === 0) {
				toast.error({
					title: "Comment required",
					description: "Please add a comment before submitting.",
				})
				return
			}

			const [offlineComment, _promise] = sdk.assetComments.add({
				content: JSON.stringify(editor.getJSON()),
				asset: asset.uuid,
				resolved: false,
			})

			if (files.length > 0) {
				void sdk.assetCommentAttachments.bulkAdd(
					files.map((file) => ({
						file,
						asset_comment: offlineComment.uuid,
					})),
				)
			}

			editor.commands.clearContent()
			setFiles([])
		},
		[asset.uuid, editor, files],
	)

	const handleReset = useCallback(() => {
		editor.commands.clearContent()
		setFiles([])
		setVisibleItemTypes(ASSET_TIMELINE_ITEM_TYPES)
	}, [editor])

	useEffect(() => {
		handleReset()
	}, [asset.uuid, handleReset])

	return (
		<section className="flex w-full flex-col gap-4">
			<div className="flex items-center justify-between gap-2">
				<Heading size="sm" accentColor="base">
					Timeline
				</Heading>

				<div className="flex items-center">
					<AssetTimelineItemTypeMultiSelectDropdown
						values={visibleItemTypes}
						onValuesChange={setVisibleItemTypes}
						size="sm"
						align="end"
					>
						<IconButton variant="ghost" size={size} accentColor="base">
							<LuIcon icon={modelIconMapping.filter} />
						</IconButton>
					</AssetTimelineItemTypeMultiSelectDropdown>
				</div>
			</div>

			<div className="flex w-full flex-col gap-4">
				{filteredTimelineItems.length > 0
					? filteredTimelineItems.map(({ type, model }) => {
							switch (type) {
								case AssetTimelineItemType.CREATED:
									return <CreatedAssetTimelineItem key={model.uuid} size={size} asset={model} />
								case AssetTimelineItemType.ATTACHMENT:
									return (
										<AssetAttachmentTimelineItem
											key={model.uuid}
											size={size}
											assetAttachment={model}
										/>
									)
								case AssetTimelineItemType.PROCEDURE:
									return <AssetProcedureTimelineItem key={model.uuid} size={size} procedure={model} />
								case AssetTimelineItemType.FIELD_VALUES:
									return (
										<AssetFieldValuesTimelineItem
											key={model.uuid}
											size={size}
											assetTypeFieldValues={model}
										/>
									)
								case AssetTimelineItemType.COMMENT:
									return (
										<AssetCommentTimelineItem key={model.uuid} size={size} assetComment={model} />
									)
								case AssetTimelineItemType.ISSUE_ASSOCIATION:
									return (
										<AssetIssueAssociationTimelineItem
											key={model.uuid}
											size={size}
											issueAssociation={model}
										/>
									)
								case AssetTimelineItemType.IDENTIFIER_VALUE:
									return (
										<AssetIdentifierValueTimelineItem
											key={model.uuid}
											size={size}
											assetTypeIdentifierValue={model}
										/>
									)
								case AssetTimelineItemType.FORM_SUBMISSION:
									return (
										<AssetTimelineFormSubmissionItem
											key={model.uuid}
											size={size}
											formSubmission={model}
										/>
									)
							}
						})
					: null}
			</div>

			<OrganizationAccessRequired accessLevel={OrganizationAccessLevel.BASIC} type="hide">
				<form className="w-full" onSubmit={handleCommentSubmit}>
					<Card className="flex h-max min-h-[72px] w-full flex-col gap-2" size="sm">
						{files.length > 0 ? (
							<CommentFileThumbnailList files={files} onDelete={handleDeleteFile} />
						) : null}

						<EditorContext value={editor}>
							<EditorContent editor={editor} className="h-max w-full grow text-sm" />
							<NewLineFloatingMenu editor={editor} />
							<TextBubbleMenu editor={editor} />
						</EditorContext>

						<ButtonGroup className="flex h-max w-full justify-end gap-2" size={size} accentColor="base">
							<AttachmentButton
								size={size}
								icon
								variant="ghost"
								type="button"
								onAttachFiles={handleAttachFiles}
							>
								<LuIcon icon={modelIconMapping.attachment} />
							</AttachmentButton>
							<IconButton aria-label="submit-comment" type="submit" variant="soft">
								<LuIcon icon={modelIconMapping.arrowUp} />
							</IconButton>
						</ButtonGroup>
					</Card>
				</form>
			</OrganizationAccessRequired>
		</section>
	)
})
AssetTimeline.displayName = "AssetTimeline"
